import { MutableRefObject, useRef } from 'react';
import { VirtualItem, useVirtual } from 'react-virtual';

import { isPinned } from './isPinned.ts';

type Props = {
  numRows: number;
  shouldPinFirstRow: boolean | number;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  overscan?: number;
  enabled?: boolean;
};

export const useVirtualizedRows = ({
  numRows,
  shouldPinFirstRow,
  containerRef,
  overscan,
  enabled = true,
}: Props): {
  virtualRows: VirtualItem[];
  virtualPaddingTop: string | null;
  virtualPaddingBottom: string | null;
  scrollToIndex: (index: number) => void;
} => {
  const pinnedItems = useRef<VirtualItem[]>([]);

  const {
    virtualItems: virtualRows,
    totalSize,
    scrollToIndex: _scrollToIndex,
  } = useVirtual({
    size: numRows,
    parentRef: containerRef,
    overscan,
  });

  if (!enabled || !shouldPinFirstRow) {
    pinnedItems.current = [];

    if (!enabled) {
      return {
        virtualRows: [],
        virtualPaddingTop: null,
        virtualPaddingBottom: null,
        scrollToIndex: () => {},
      };
    }
  }

  const virtualRowsWithPinnedRow = [...virtualRows];

  // If the first row should be pinned, this will prepend it to each window of virtualized rows
  // This ensures that the sticky positioning UI logic works correctly
  if (shouldPinFirstRow) {
    const hasPinnedRow = pinnedItems.current.length > 0;
    if (!hasPinnedRow) {
      for (const row of virtualRows) {
        if (isPinned(shouldPinFirstRow, row.index)) {
          pinnedItems.current.push(row);
        }
      }
    }

    const isWindowPastFirstRow = virtualRows[0]?.index !== 0;
    if (isWindowPastFirstRow && hasPinnedRow) {
      virtualRowsWithPinnedRow.unshift(...pinnedItems.current);
    }
  }

  // This calculates the top and bottom padding for the virtualized container
  // This logic is from https://tanstack.com/table/v8/docs/examples/react/virtualized-rows
  const virtualPaddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const virtualPaddingBottom =
    virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;

  return {
    virtualRows: virtualRowsWithPinnedRow,
    virtualPaddingTop: virtualPaddingTop > 0 ? `${virtualPaddingTop}px` : null,
    virtualPaddingBottom: virtualPaddingBottom > 0 ? `${virtualPaddingBottom}px` : null,
    scrollToIndex: (index: number) => _scrollToIndex(index, { align: 'center' }),
  };
};
